import { ConfigService } from '@app-core/services/config.service';
import { ProductFilter, defaultProductFilterSetup } from '@app-model/filters/product-filter';
import { ProductFilters } from '@app-model/filters/product-filters';
import { ProductItem } from '@app-model/response';

import { planCodeMetal, planMatch } from './plan-utils';

export function fetchFilters(products: ProductItem[]): ProductFilters {
  return products
    ? new ProductFilters(
        fetchProductFilters(products),
        fetchPlanFilters(products),
        fetchDeductibleFilters(products),
        fetchTermFilters(products),
        fetchMileageFilters(products)
      )
    : new ProductFilters();
}

function fetchProductFilters(products: ProductItem[]): ProductFilter[] {
  const filters = products.reduce(
    (prevVal, product) => {
      const index = prevVal.some((item) => item.name === product.productDescription.enUsProductDescription);
      if (!index) {
        prevVal.push({
          ...defaultProductFilterSetup,
          name: product.productDescription.enUsProductDescription,
          productCode: product.productCode,
          productCategory: product.productCategory,
          property: 'product',
        });
      }

      return prevVal;
    },
    <ProductFilter[]>[]
  );

  return sortStrings<ProductFilter>(filters);
}

function fetchPlanFilters(products: ProductItem[]): ProductFilter[] {
  const filters = products
    .reduce(
      (acc, product) => {
        if (!acc.find((plan: ProductFilter) => planMatch(plan, product))) {
          acc.push(createPlanFilter(product));
        }

        return acc;
      },
      <ProductFilter[]>[]
    )
    .sort((a: ProductFilter, b: ProductFilter) => a.sortLevel - b.sortLevel);

  return filters;
}

function fetchDeductibleFilters(products: ProductItem[]): ProductFilter[] {
  const filters = products.reduce(
    (prevVal, elem) => {
      const index = prevVal.some((item) => {
        let deductibleName = elem.deductible;
        if (elem.disappearingDeductible) {
          deductibleName = '' + elem.deductible + '(D)';
        }

        return item.name === deductibleName;
      });
      if (!index) {
        let deductibleName = elem.deductible;
        if (elem.disappearingDeductible) {
          deductibleName = '' + elem.deductible + '(D)';
        }
        prevVal.push({ ...defaultProductFilterSetup, name: deductibleName, property: 'deductible' });
      }

      return prevVal;
    },
    <ProductFilter[]>[]
  );

  return sortNumericalStrings<ProductFilter>(filters);
}

function fetchTermFilters(products: ProductItem[]): ProductFilter[] {
  const filters = products.reduce((prevVal, elem) => {
    const index = prevVal.some((item) => {
      return item.name === elem.maxTerm;
    });
    if (!index) {
      prevVal.push({ ...defaultProductFilterSetup, name: elem.maxTerm, property: 'term' });
    }

    return prevVal;
  }, []);

  return sortNumericalStrings<ProductFilter>(filters);
}

function fetchMileageFilters(products: ProductItem[]): ProductFilter[] {
  const filters = products.reduce(
    (prevVal, elem) => {
      const index = prevVal.some((item) => item.name === elem.termMileage);
      if (!index && Number(elem.termMileage) !== 0) {
        prevVal.push({ ...defaultProductFilterSetup, name: elem.termMileage, property: 'mileage' });
      }

      return prevVal;
    },
    <ProductFilter[]>[]
  );

  return sortNumericalStrings<ProductFilter>(filters);
}

function sortNumericalStrings<T>(arrayToSort: T[]): T[] {
  arrayToSort.sort(function (a, b) {
    const nameA = Number(a['name']);
    const nameB = Number(b['name']);

    if (nameA < nameB) {
      // sort string ascending
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }

    return 0; // default return value (no sorting)
  });

  return arrayToSort;
}

function sortStrings<T>(arrayToSort: T[]): T[] {
  arrayToSort.sort(function (a, b) {
    const nameA = a['name'].toLowerCase();
    const nameB = b['name'].toLowerCase();

    if (nameA < nameB) {
      // sort string ascending
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }

    return 0; // default return value (no sorting)
  });

  return arrayToSort;
}

function createPlanFilter(product: ProductItem): ProductFilter {
  let sortLevel = 2;
  let name = product.planName.enUsPlanName;
  if (product.productCategory === 'MULTICOVERAGE') {
    if (product.isBundled) {
      if (product.isDiscounted) {
        if (
          ConfigService.getEnvironment().features.bundleMultiCoveragePlansByMetal &&
          planCodeMetal(product.planCode)
        ) {
          name = planCodeMetal(product.planCode);
        } else {
          name = 'BUNDLES';
          sortLevel = 1;
        }
      } else {
        name = 'PRODUCT COMBINATIONS';
        sortLevel = 3;
      }
    }
  }

  return {
    ...defaultProductFilterSetup,
    property: 'plan',
    name,
    productCode: product.productCode,
    planCode: product.planCode,
    productCategory: product.productCategory,
    sortLevel,
  };
}
